'use client';

import { useState } from 'react';
import MapInterface from './MapInterface';
import DataVisualization from './DataVisualization';
import ResearchReports from './ResearchReports';
import AreaInsights from './AreaInsights';
import MarketAnalysis from './MarketAnalysis';
import ExpertCommentary from './ExpertCommentary';
import FilterSystem from './FilterSystem';

interface Area {
  id: string;
  name: string;
  description: string;
  coordinates: {lat: number;lng: number;};
  stats: {
    population: string;
    avgPropertyPrice: string;
    growthRate: string;
  };
}

interface ChartData {
  name: string;
  value: number;
}

interface Report {
  id: string;
  title: string;
  description: string;
  category: string;
  publishDate: string;
  author: string;
  downloadUrl: string;
  coverImage: string;
  coverImageAlt: string;
  pages: number;
}

interface AreaInsight {
  id: string;
  name: string;
  description: string;
  stats: Array<{
    label: string;
    value: string;
    icon: string;
  }>;
  highlights: string[];
}

interface MarketIndicator {
  id: string;
  title: string;
  value: string;
  change: string;
  trend: 'up' | 'down' | 'stable';
  description: string;
}

interface Expert {
  id: string;
  name: string;
  title: string;
  image: string;
  imageAlt: string;
  commentary: string;
  expertise: string[];
  publishDate: string;
}

interface FilterOption {
  id: string;
  label: string;
  count: number;
}

const CityInsightsInteractive = () => {
  const [selectedArea, setSelectedArea] = useState<Area | null>(null);

  const areas: Area[] = [
  {
    id: "hitech-city",
    name: "Hitech City",
    description: "Hyderabad's premier IT hub with world-class infrastructure, multinational corporations, and modern residential complexes. Known for its cosmopolitan culture and excellent connectivity.",
    coordinates: { lat: 17.4435, lng: 78.3772 },
    stats: {
      population: "2.5 Lakhs",
      avgPropertyPrice: "₹8,500/sq.ft",
      growthRate: "+12% YoY"
    }
  },
  {
    id: "gachibowli",
    name: "Gachibowli",
    description: "Fast-growing tech corridor with major IT parks, educational institutions, and upscale residential developments. Excellent social infrastructure and entertainment options.",
    coordinates: { lat: 17.4399, lng: 78.3489 },
    stats: {
      population: "1.8 Lakhs",
      avgPropertyPrice: "₹7,200/sq.ft",
      growthRate: "+15% YoY"
    }
  },
  {
    id: "banjara-hills",
    name: "Banjara Hills",
    description: "Premium residential locality known for luxury properties, diplomatic presence, and high-end retail. One of Hyderabad's most prestigious addresses with excellent amenities.",
    coordinates: { lat: 17.4239, lng: 78.4738 },
    stats: {
      population: "1.2 Lakhs",
      avgPropertyPrice: "₹12,000/sq.ft",
      growthRate: "+8% YoY"
    }
  },
  {
    id: "jubilee-hills",
    name: "Jubilee Hills",
    description: "Elite residential area housing celebrities, business leaders, and diplomats. Features luxury villas, high-end apartments, and exclusive clubs with top-tier infrastructure.",
    coordinates: { lat: 17.4326, lng: 78.4071 },
    stats: {
      population: "95,000",
      avgPropertyPrice: "₹15,000/sq.ft",
      growthRate: "+6% YoY"
    }
  },
  {
    id: "kondapur",
    name: "Kondapur",
    description: "Emerging residential and commercial hub with excellent connectivity to IT corridors. Offers diverse housing options and growing social infrastructure.",
    coordinates: { lat: 17.4617, lng: 78.3617 },
    stats: {
      population: "1.5 Lakhs",
      avgPropertyPrice: "₹6,800/sq.ft",
      growthRate: "+18% YoY"
    }
  },
  {
    id: "madhapur",
    name: "Madhapur",
    description: "Strategic location connecting major IT hubs with vibrant commercial activity. Mix of residential and commercial properties with excellent amenities and entertainment options.",
    coordinates: { lat: 17.4474, lng: 78.3914 },
    stats: {
      population: "2.1 Lakhs",
      avgPropertyPrice: "₹7,500/sq.ft",
      growthRate: "+14% YoY"
    }
  }];


  const populationData: ChartData[] = [
  { name: "2019", value: 95 },
  { name: "2020", value: 102 },
  { name: "2021", value: 108 },
  { name: "2022", value: 115 },
  { name: "2023", value: 123 },
  { name: "2024", value: 132 },
  { name: "2025", value: 140 }];


  const propertyTrends: ChartData[] = [
  { name: "2019", value: 5.2 },
  { name: "2020", value: 5.5 },
  { name: "2021", value: 6.1 },
  { name: "2022", value: 6.8 },
  { name: "2023", value: 7.5 },
  { name: "2024", value: 8.2 },
  { name: "2025", value: 9.1 }];


  const sectorDistribution: ChartData[] = [
  { name: "IT & Technology", value: 35 },
  { name: "Real Estate", value: 25 },
  { name: "Healthcare", value: 15 },
  { name: "Education", value: 12 },
  { name: "Retail & Services", value: 13 }];


  const reports: Report[] = [
  {
    id: "report-1",
    title: "Hyderabad Real Estate Market Report 2025",
    description: "Comprehensive analysis of property trends, pricing patterns, and investment opportunities across Hyderabad's key residential and commercial zones.",
    category: "property",
    publishDate: "December 2025",
    author: "MileDeep.ai Research Team",
    downloadUrl: "#",
    coverImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1b6d5c626-1765064011199.png",
    coverImageAlt: "Modern high-rise residential buildings with glass facades against blue sky in Hyderabad",
    pages: 48
  },
  {
    id: "report-2",
    title: "Urban Development Patterns: A Decade of Growth",
    description: "Ten-year retrospective examining infrastructure development, population migration, and urban planning initiatives that shaped modern Hyderabad.",
    category: "development",
    publishDate: "November 2025",
    author: "Dr. Rajesh Kumar",
    downloadUrl: "#",
    coverImage: "https://images.unsplash.com/photo-1618245894981-59d132cc1aff",
    coverImageAlt: "Aerial view of urban development showing roads, buildings and infrastructure planning",
    pages: 62
  },
  {
    id: "report-3",
    title: "IT Corridor Economic Impact Study",
    description: "Detailed assessment of economic contributions from Hyderabad's IT sector, including employment generation, infrastructure development, and ancillary business growth.",
    category: "economy",
    publishDate: "October 2025",
    author: "Economic Research Institute",
    downloadUrl: "#",
    coverImage: "https://img.rocket.new/generatedImages/rocket_gen_img_122e81c8d-1766056134802.png",
    coverImageAlt: "Computer code on multiple screens in modern tech office environment",
    pages: 55
  },
  {
    id: "report-4",
    title: "Residential Preferences Survey 2025",
    description: "Survey-based insights into homebuyer preferences, amenity priorities, and location choices among Hyderabad's diverse demographic segments.",
    category: "property",
    publishDate: "September 2025",
    author: "Urban Living Research Group",
    downloadUrl: "#",
    coverImage: "https://images.unsplash.com/photo-1541027433402-45325b550c51",
    coverImageAlt: "Modern apartment interior with spacious living room, large windows and contemporary furniture",
    pages: 38
  },
  {
    id: "report-5",
    title: "Infrastructure Investment Analysis",
    description: "Evaluation of public and private infrastructure investments, their impact on connectivity, and implications for future urban expansion.",
    category: "development",
    publishDate: "August 2025",
    author: "Infrastructure Policy Forum",
    downloadUrl: "#",
    coverImage: "https://images.unsplash.com/photo-1583996829982-823143cc975a",
    coverImageAlt: "Highway interchange with multiple levels showing modern infrastructure development",
    pages: 71
  },
  {
    id: "report-6",
    title: "Commercial Real Estate Outlook 2026",
    description: "Forward-looking analysis of office space demand, retail trends, and commercial property investment opportunities in Hyderabad's evolving business landscape.",
    category: "economy",
    publishDate: "December 2025",
    author: "Commercial Property Analysts",
    downloadUrl: "#",
    coverImage: "https://images.unsplash.com/photo-1667384449582-3ecd5717fa19",
    coverImageAlt: "Modern office lobby with glass walls, contemporary seating and professional business environment",
    pages: 44
  }];


  const areaInsights: AreaInsight[] = [
  {
    id: "insight-1",
    name: "Hitech City & Gachibowli Corridor",
    description: "The western IT corridor continues to dominate Hyderabad's growth story with sustained infrastructure development, rising property values, and expanding commercial activity. This area represents the city's economic engine with over 400 IT companies and 5 lakh+ workforce.",
    stats: [
    { label: "IT Companies", value: "400+", icon: "BuildingOfficeIcon" },
    { label: "Workforce", value: "5L+", icon: "UsersIcon" },
    { label: "Avg Rent", value: "₹35K", icon: "HomeIcon" },
    { label: "Connectivity", value: "9.5/10", icon: "MapPinIcon" }],

    highlights: [
    "Metro connectivity to major residential areas completed in 2024",
    "New commercial developments adding 8 million sq.ft office space",
    "Premium residential projects with 15-20% price appreciation annually",
    "World-class social infrastructure including international schools and hospitals",
    "Emerging as preferred location for multinational headquarters"]

  },
  {
    id: "insight-2",
    name: "Banjara Hills & Jubilee Hills",
    description: "Hyderabad's most prestigious residential addresses maintain their elite status with limited supply driving sustained value appreciation. These areas combine luxury living with excellent social infrastructure and diplomatic presence.",
    stats: [
    { label: "Luxury Villas", value: "2,500+", icon: "HomeModernIcon" },
    { label: "Avg Property Value", value: "₹12Cr", icon: "CurrencyRupeeIcon" },
    { label: "Green Cover", value: "35%", icon: "SparklesIcon" },
    { label: "Exclusivity", value: "10/10", icon: "StarIcon" }],

    highlights: [
    "Home to diplomatic missions, celebrities, and business leaders",
    "Limited new construction maintaining exclusivity and property values",
    "Premium clubs, fine dining, and luxury retail within walking distance",
    "Excellent schools including international curriculum institutions",
    "Heritage bungalows being converted to boutique commercial spaces"]

  }];


  const marketIndicators: MarketIndicator[] = [
  {
    id: "indicator-1",
    title: "Residential Property Prices",
    value: "₹8,200/sq.ft",
    change: "+11.5%",
    trend: "up",
    description: "Average residential property prices across prime locations show sustained growth driven by strong demand from IT professionals and limited supply in established neighborhoods. Premium segments outperforming mid-range properties."
  },
  {
    id: "indicator-2",
    title: "Commercial Office Absorption",
    value: "12.5M sq.ft",
    change: "+18.2%",
    trend: "up",
    description: "Office space absorption reached record levels with IT sector expansion and multinational companies establishing larger presence. Grade A office space commanding premium rentals with occupancy rates above 90%."
  },
  {
    id: "indicator-3",
    title: "Rental Yields",
    value: "3.2%",
    change: "-0.3%",
    trend: "down",
    description: "Rental yields showing slight compression as property prices appreciate faster than rental rates. However, premium locations maintain healthy yields with strong tenant demand and minimal vacancy periods."
  },
  {
    id: "indicator-4",
    title: "Infrastructure Investment",
    value: "₹45,000 Cr",
    change: "+25%",
    trend: "up",
    description: "Public and private infrastructure investments accelerating with metro expansion, road widening projects, and new commercial developments. Government focus on improving connectivity to emerging growth corridors."
  },
  {
    id: "indicator-5",
    title: "IT Sector Employment",
    value: "8.5 Lakh",
    change: "+12%",
    trend: "up",
    description: "IT sector employment continues robust growth with major hiring across technology companies. Hyderabad maintaining position as India's second-largest IT hub with expanding talent pool and competitive operational costs."
  },
  {
    id: "indicator-6",
    title: "Retail Space Demand",
    value: "4.2M sq.ft",
    change: "0%",
    trend: "stable",
    description: "Retail space demand stabilizing after pandemic recovery with focus shifting to experiential retail and entertainment. High-street retail in premium locations showing resilience while suburban malls face challenges."
  }];


  const experts: Expert[] = [
  {
    id: "expert-1",
    name: "Dr. Ananya Reddy",
    title: "Urban Planning Specialist & Former GHMC Advisor",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f1230b9d-1763294070661.png",
    imageAlt: "Professional woman with short black hair in navy blazer smiling at camera in modern office",
    commentary: "Hyderabad's growth trajectory over the past decade has been remarkable, but we're now at a critical juncture. The western corridor's success has created imbalanced development patterns. Our research indicates that the next phase must focus on decentralizing growth through strategic infrastructure investments in the northern and eastern corridors. The proposed Regional Ring Road and metro extensions will be game-changers, but we need coordinated planning to prevent the mistakes of unchecked expansion. The city's future depends on sustainable, inclusive development that doesn't compromise livability for growth.",
    expertise: ["Urban Development", "Infrastructure Planning", "Policy Analysis"],
    publishDate: "15 December 2025"
  },
  {
    id: "expert-2",
    name: "Rajesh Kumar",
    title: "Real Estate Market Analyst & Investment Advisor",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_112193a91-1763294779964.png",
    imageAlt: "Middle-aged man with gray hair in dark suit standing in modern corporate office",
    commentary: "The Hyderabad real estate market is experiencing a maturation phase where fundamentals matter more than speculation. We're seeing a clear bifurcation - premium properties in established locations continue appreciating steadily, while peripheral areas face correction. The key insight for investors is location selectivity. Areas with confirmed metro connectivity, established social infrastructure, and proximity to employment hubs will outperform. The rental market is particularly interesting - while yields have compressed, quality properties in IT corridors maintain strong tenant demand with minimal vacancy. For long-term investors, this remains one of India's most promising markets.",
    expertise: ["Market Analysis", "Investment Strategy", "Property Valuation"],
    publishDate: "10 December 2025"
  }];


  const filterAreas: FilterOption[] = [
  { id: "all", label: "All Areas", count: 156 },
  { id: "hitech-city", label: "Hitech City", count: 28 },
  { id: "gachibowli", label: "Gachibowli", count: 24 },
  { id: "banjara-hills", label: "Banjara Hills", count: 18 },
  { id: "jubilee-hills", label: "Jubilee Hills", count: 15 },
  { id: "kondapur", label: "Kondapur", count: 22 }];


  const filterTopics: FilterOption[] = [
  { id: "all", label: "All Topics", count: 156 },
  { id: "property", label: "Property Market", count: 45 },
  { id: "development", label: "Urban Development", count: 38 },
  { id: "economy", label: "Economic Trends", count: 32 },
  { id: "infrastructure", label: "Infrastructure", count: 41 }];


  const filterContentTypes: FilterOption[] = [
  { id: "all", label: "All Types", count: 156 },
  { id: "report", label: "Research Reports", count: 42 },
  { id: "analysis", label: "Market Analysis", count: 38 },
  { id: "insight", label: "Area Insights", count: 35 },
  { id: "commentary", label: "Expert Commentary", count: 41 }];


  const handleAreaSelect = (area: Area) => {
    setSelectedArea(area);
  };

  const handleFilterChange = (filters: any) => {
    console.log('Filters applied:', filters);
  };

  return (
    <div data-component-id="src/app/city-insights-dashboard/components/CityInsightsInteractive.tsx:402:4" data-component-path="src/app/city-insights-dashboard/components/CityInsightsInteractive.tsx" data-component-line="402" data-component-end-line="419" data-component-file="CityInsightsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-16%22%7D" className="space-y-16">
      <MapInterface data-component-id="src/app/city-insights-dashboard/components/CityInsightsInteractive.tsx:403:6" data-component-path="src/app/city-insights-dashboard/components/CityInsightsInteractive.tsx" data-component-line="403" data-component-end-line="403" data-component-file="CityInsightsInteractive.tsx" data-component-name="MapInterface" data-component-content="%7B%22elementName%22%3A%22MapInterface%22%7D" areas={areas} onAreaSelect={handleAreaSelect} />
      <DataVisualization data-component-id="src/app/city-insights-dashboard/components/CityInsightsInteractive.tsx:404:6" data-component-path="src/app/city-insights-dashboard/components/CityInsightsInteractive.tsx" data-component-line="404" data-component-end-line="407" data-component-file="CityInsightsInteractive.tsx" data-component-name="DataVisualization" data-component-content="%7B%22elementName%22%3A%22DataVisualization%22%7D"
        populationData={populationData}
        propertyTrends={propertyTrends}
        sectorDistribution={sectorDistribution} />

      <FilterSystem data-component-id="src/app/city-insights-dashboard/components/CityInsightsInteractive.tsx:409:6" data-component-path="src/app/city-insights-dashboard/components/CityInsightsInteractive.tsx" data-component-line="409" data-component-end-line="413" data-component-file="CityInsightsInteractive.tsx" data-component-name="FilterSystem" data-component-content="%7B%22elementName%22%3A%22FilterSystem%22%7D"
        areas={filterAreas}
        topics={filterTopics}
        contentTypes={filterContentTypes}
        onFilterChange={handleFilterChange} />

      <ResearchReports data-component-id="src/app/city-insights-dashboard/components/CityInsightsInteractive.tsx:415:6" data-component-path="src/app/city-insights-dashboard/components/CityInsightsInteractive.tsx" data-component-line="415" data-component-end-line="415" data-component-file="CityInsightsInteractive.tsx" data-component-name="ResearchReports" data-component-content="%7B%22elementName%22%3A%22ResearchReports%22%7D" reports={reports} />
      <AreaInsights data-component-id="src/app/city-insights-dashboard/components/CityInsightsInteractive.tsx:416:6" data-component-path="src/app/city-insights-dashboard/components/CityInsightsInteractive.tsx" data-component-line="416" data-component-end-line="416" data-component-file="CityInsightsInteractive.tsx" data-component-name="AreaInsights" data-component-content="%7B%22elementName%22%3A%22AreaInsights%22%7D" insights={areaInsights} />
      <MarketAnalysis data-component-id="src/app/city-insights-dashboard/components/CityInsightsInteractive.tsx:417:6" data-component-path="src/app/city-insights-dashboard/components/CityInsightsInteractive.tsx" data-component-line="417" data-component-end-line="417" data-component-file="CityInsightsInteractive.tsx" data-component-name="MarketAnalysis" data-component-content="%7B%22elementName%22%3A%22MarketAnalysis%22%7D" indicators={marketIndicators} />
      <ExpertCommentary data-component-id="src/app/city-insights-dashboard/components/CityInsightsInteractive.tsx:418:6" data-component-path="src/app/city-insights-dashboard/components/CityInsightsInteractive.tsx" data-component-line="418" data-component-end-line="418" data-component-file="CityInsightsInteractive.tsx" data-component-name="ExpertCommentary" data-component-content="%7B%22elementName%22%3A%22ExpertCommentary%22%7D" experts={experts} />
    </div>);

};

export default CityInsightsInteractive;